import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormLabel,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Grid,
  Box,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import { TemplateVariablePicker } from './TemplateVariablePicker';
import { SmsTemplate, SmsTemplateCreate, SmsTemplateUpdate } from '@/types/sms';
import { RootState } from '@/store/store';
import { formatErrorForDisplay } from '@/utils/errorUtils';
import {
  createTemplateRequested,
  updateTemplateRequested,
  clearTemplateErrors,
} from '@/store/slices/smsTemplateSlice';

interface TemplateEditDialogProps {
  open: boolean;
  onClose: () => void;
  template?: SmsTemplate | null;
  mode: 'create' | 'edit';
}

export const TemplateEditDialog: React.FC<TemplateEditDialogProps> = ({
  open,
  onClose,
  template,
  mode,
}) => {
  const dispatch = useDispatch();
  const contentRef = useRef<HTMLTextAreaElement>(null);
  
  const { createLoading, createError, updateLoading, updateError } = useSelector(
    (state: RootState) => state.smsTemplate
  );

  const [formData, setFormData] = useState({
    name: '',
    lob_type: 'ALL' as 'MOTOR' | 'HEALTH' | 'ALL',
    sms_type: 'notice_only' as 'notice_only' | 'notice_with_link',
    content: '',
    character_limit: 160,
    is_active: true,
  });

  const [extractedVariables, setExtractedVariables] = useState<string[]>([]);
  const [segmentCount, setSegmentCount] = useState(0);

  useEffect(() => {
    if (template && mode === 'edit') {
      setFormData({
        name: template.name,
        lob_type: template.lob_type,
        sms_type: template.sms_type,
        content: template.content,
        character_limit: template.character_limit,
        is_active: template.is_active,
      });
    } else {
      // Reset for create mode
      setFormData({
        name: '',
        lob_type: 'ALL',
        sms_type: 'notice_only',
        content: '',
        character_limit: 160,
        is_active: true,
      });
    }
  }, [template, mode, open]);

  useEffect(() => {
    // Extract variables from content
    const variablePattern = /\{#(\w+)#\}/g;
    const matches = formData.content.match(variablePattern);
    const variables = matches ? matches.map(m => m.replace(/\{#|#\}/g, '')) : [];
    setExtractedVariables([...new Set(variables)]);

    // Calculate SMS segments
    const length = formData.content.length;
    const segments = length === 0 ? 0 : length <= 160 ? 1 : Math.ceil(length / 153);
    setSegmentCount(segments);
  }, [formData.content]);

  const handleVariableInsert = (variable: string) => {
    if (contentRef.current) {
      const start = contentRef.current.selectionStart;
      const end = contentRef.current.selectionEnd;
      const currentContent = formData.content;
      
      const newContent =
        currentContent.substring(0, start) +
        variable +
        currentContent.substring(end);
      
      setFormData({ ...formData, content: newContent });
      
      // Set cursor position after inserted variable
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.selectionStart = start + variable.length;
          contentRef.current.selectionEnd = start + variable.length;
          contentRef.current.focus();
        }
      }, 0);
    }
  };

  const handleSubmit = () => {
    if (mode === 'create') {
      const createData: SmsTemplateCreate = {
        name: formData.name,
        lob_type: formData.lob_type,
        sms_type: formData.sms_type,
        content: formData.content,
        variables: extractedVariables,
        character_limit: formData.character_limit,
        is_active: formData.is_active,
      };
      dispatch(createTemplateRequested(createData));
    } else if (template) {
      const updateData: SmsTemplateUpdate = {
        name: formData.name,
        lob_type: formData.lob_type,
        sms_type: formData.sms_type,
        content: formData.content,
        variables: extractedVariables,
        character_limit: formData.character_limit,
        is_active: formData.is_active,
      };
      dispatch(updateTemplateRequested({ id: template.id, data: updateData }));
    }
  };

  const handleClose = () => {
    dispatch(clearTemplateErrors());
    onClose();
  };

  useEffect(() => {
    // Close dialog on successful create/update
    if (open && !createLoading && !updateLoading && !createError && !updateError) {
      // Check if we just completed an action
      if (mode === 'create' || mode === 'edit') {
        const timer = setTimeout(() => {
          handleClose();
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [createLoading, updateLoading, createError, updateError]);

  const isLoading = createLoading || updateLoading;
  const error = createError || updateError;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        {mode === 'create' ? 'Create New SMS Template' : 'Edit SMS Template'}
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <DialogContentText sx={{ marginBottom: 1.5, fontSize: '0.875rem' }}>
          {mode === 'create'
            ? 'Create a new SMS template for renewal notice communications'
            : 'Update the SMS template details'}
        </DialogContentText>

        <Grid container spacing={2}>
          {/* Form Section */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {error && (
                <Alert severity="error">{formatErrorForDisplay(error)}</Alert>
              )}

              <TextField
                label="Template Name"
                required
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Renewal_Notice_SMS_240925"
                disabled={isLoading}
              />

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Line of Business (LOB)</InputLabel>
                    <Select
                      value={formData.lob_type}
                      label="Line of Business (LOB)"
                      onChange={(e) =>
                        setFormData({ ...formData, lob_type: e.target.value as 'MOTOR' | 'HEALTH' | 'ALL' })
                      }
                      disabled={isLoading}
                    >
                      <MenuItem value="ALL">ALL</MenuItem>
                      <MenuItem value="MOTOR">MOTOR</MenuItem>
                      <MenuItem value="HEALTH">HEALTH</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>SMS Type</InputLabel>
                    <Select
                      value={formData.sms_type}
                      label="SMS Type"
                      onChange={(e) =>
                        setFormData({ ...formData, sms_type: e.target.value as 'notice_only' | 'notice_with_link' })
                      }
                      disabled={isLoading}
                    >
                      <MenuItem value="notice_only">Notice Only</MenuItem>
                      <MenuItem value="notice_with_link">Notice + Journey Link</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box>
                <FormLabel required sx={{ marginBottom: 0.5, display: 'block' }}>
                  Template Content
                </FormLabel>
                <TextField
                  inputRef={contentRef}
                  multiline
                  rows={4}
                  fullWidth
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Enter SMS template with {#variable#} placeholders..."
                  disabled={isLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'monospace',
                    }
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {formData.content.length} characters
                    {segmentCount > 0 && ` (${segmentCount} SMS segment${segmentCount > 1 ? 's' : ''})`}
                  </Typography>
                  {segmentCount > 3 && (
                    <Typography variant="caption" color="error">
                      Warning: Exceeds 3 segments (480 chars)
                    </Typography>
                  )}
                </Box>
              </Box>

              {extractedVariables.length > 0 && (
                <Box>
                  <FormLabel sx={{ marginBottom: 0.5, display: 'block' }}>
                    Extracted Variables
                  </FormLabel>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {extractedVariables.map((variable) => (
                      <Chip
                        key={variable}
                        label={`{#${variable}#}`}
                        size="small"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '12px',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    disabled={isLoading}
                  />
                }
                label="Active Template"
              />
            </Box>
          </Grid>

          {/* Variable Picker Section */}
          <Grid item xs={12} md={4}>
            <Box sx={{ maxHeight: 'calc(90vh - 200px)', height: '100%' }}>
              <TemplateVariablePicker onVariableInsert={handleVariableInsert} />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={isLoading} 
          variant="contained"
          sx={{ backgroundColor: '#003087' }}
        >
          {isLoading
            ? 'Saving...'
            : mode === 'create'
            ? 'Create Template'
            : 'Update Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
